/**
 * WolIcons.tsx — the three brand-kit icons the Wall of Love uses, inlined
 * verbatim from Brand/BrandAssets/icons/utility-set-v1/ (paths unchanged).
 * Inline, not <img src="*.svg">, because each takes its colour from the page
 * via currentColor (BRAND-RULES: no <img> for colour-flexible icons). Same
 * convention as app/pro-member/_v12/sections/PM12Quote.tsx.
 */

/** icon-star.svg (Aria, 2026-09-24) — read-only rating glyph; fill set by .wl-stars svg. */
export function StarIcon() {
  return (
    <svg
      viewBox="0 0 22 22"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M11 1.8l2.55 5.35 5.75.72-4.2 4.05.98 5.73L11 14.75l-5.08 2.9.98-5.73-4.2-4.05 5.75-.72z" />
    </svg>
  );
}

/** icon-youtube-v4.svg (Style D utility icon). */
export function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 22 22"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5.8 2.3 C3.4 2.2 2.2 3.5 2.3 5.7 L2.3 16.3 C2.4 18.5 3.8 19.8 6.0 19.7 L16.0 19.8 C18.3 19.7 19.7 18.4 19.6 16.2 L19.7 5.8 C19.6 3.5 18.2 2.3 15.9 2.2 Z" />
      <path d="M7.5 6.8 C7.2 9.0 7.2 13.0 7.5 15.2" />
      <path d="M7.5 6.8 C10.2 8.2 13.4 9.8 15.8 11.1" />
      <path d="M15.8 11.1 C13.3 12.4 10.1 14.0 7.5 15.2" />
    </svg>
  );
}

/**
 * Speed pass 2026-09-28: the wall draws ~390 stars. Each used to inline the whole
 * glyph (~340 bytes, twice: HTML + RSC payload). Now the glyph is defined ONCE by
 * <StarSprite /> (rendered at the top of /wall-of-love) and every star is a <use>
 * of it — same viewBox, size, path and stroke attributes; fill/stroke still come
 * from `.wl-stars svg` and inherit into the <use>. StarIcon above is unchanged
 * (ReviewModal on other pages uses it, where no sprite exists).
 */
export function StarSprite() {
  return (
    <svg width={0} height={0} aria-hidden="true" focusable="false" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
      <symbol id="wl-star" viewBox="0 0 22 22">
        <path
          d="M11 1.8l2.55 5.35 5.75.72-4.2 4.05.98 5.73L11 14.75l-5.08 2.9.98-5.73-4.2-4.05 5.75-.72z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
        />
      </symbol>
    </svg>
  );
}

/** Stars row — drawn only for a rating of 1–5 (0 / null = none, per brief). Needs <StarSprite /> on the page. */
export function Stars({ rating, label }: { rating: number | null; label?: boolean }) {
  if (!rating || rating < 1) return null;
  const n = Math.min(5, Math.round(rating));
  return (
    <div className="wl-stars" role={label ? 'img' : undefined} aria-label={label ? `${n} / 5` : undefined} aria-hidden={label ? undefined : true}>
      {Array.from({ length: n }, (_, i) => (
        <svg key={i} viewBox="0 0 22 22" width={16} height={16} aria-hidden="true" focusable="false">
          <use href="#wl-star" />
        </svg>
      ))}
    </div>
  );
}
